/**
 * Similarities between `Movie` and `PictureSet`
 *
 * @module
 * @format
 */

import {UPGRADE} from '@acng/frontend-stargazer';
import {LocalActor} from '@acng/frontend-relativity';

/**
 * @import {Amateur} from '../../amateurPool/model/amateur.js';
 * @import {GalleryController, GalleryData} from '../../gallery/types';
 */

import {ABSTRACT, guard, STRING, TUPLE} from '@acng/frontend-rubicon';
import {loadImage} from '@acng/frontend-bounty';
import {each} from '@acng/frontend-bounty/std/array.js';
import {POSSIBLE_FSK_LEVELS_DESC} from './fsk.js';
import {user} from '../../user/user.js';
import {fskTrans} from 'acng/userPool/0fsk.js';
import {media} from '../service/env.js';
import {getFallbackImageUrl} from 'acng/layout/service/fallback-image.js';
import {get} from '../../amateurPool/service/cache.js';
import {frigg} from '../../core/model/image.js';
import {ctxGallery} from '../context/gallery.js';

const provideGallery = LocalActor(ctxGallery);

/**
 * @implements {GalleryController}
 */
export class Gallery {
  /**
   * @param {GalleryData} data
   */
  constructor(data) {
    ASSERT: {
      guard(this, ABSTRACT(Gallery));

      this.set_id = data.set_id;
      this.user_id = data.user_id;

      /**
       * The _primary_ nickname.
       *
       * **DO NOT DISPLAY THIS ONE**.
       *
       * Use {@link Amateur.getNickname} to get the one to display.
       */
      this.nickname = data.nickname;
      this.name = data.name;
      this.description = data.description;

      /**
       * Timestamp in milliseconds
       */
      this.published = data.published;

      /**
       * The _coin_ price
       */
      this.price = data.price;

      /**
       * The number of reviews. (0+)
       */
      this.votes = data.votes;

      /**
       * The average rating. (1-5 with votes, 0 without)
       */
      this.rating = data.rating;
      this.category_codes = data.category_codes;
      this.image_fsk18 = data.image_fsk18;
      this.image_fsk16 = data.image_fsk16;
      this.image_fsk12 = data.image_fsk12;
      this.media_rating = data.media_rating;
      this.media_rating_test = data.media_rating_test;
      this.fsk = data.fsk;

      /**
       * If set to `true`, the gallery is no longer listed.
       *
       * Though it is still available for customers who have already purchased it. NOT part of the solr
       * profile, eventually set by the "blocked-api" or the "stock-api"
       */
      this.blocked = data.blocked;
    }
  }

  /** @type {Promise<Amateur>} */
  get creator() {
    return get(`${this.user_id}`);
  }

  /** @deprecated Use {@link Gallery.creator} */
  getAmateur() {
    return get(`${this.user_id}`);
  }

  /**
   * @returns {string} Absolute image source URL
   */
  @frigg getImageUrl() {
    for (const level of POSSIBLE_FSK_LEVELS_DESC) {
      const value = this[GALLERY_FSK_IMAGE_MAP[level]];

      if (value && user.fsk >= level) {
        return `${media.content.pictures}${value}`;
      }
    }

    return getFallbackImageUrl();
  }

  /**
   * @type {string} Absolute URL
   */
  get image() {
    return this.getImageUrl();
  }

  async getImage() {
    return await loadImage(this.getImageUrl(), getFallbackImageUrl());
  }

  /** @deprecated Use {@link Gallery.title} */
  getTitle() {
    return fskTrans(this.name);
  }

  /**
   * > The `name` property is blocked by {@link GalleryData}.
   *
   * @type {string | null}
   */
  get title() {
    return fskTrans(this.name);
  }

  /** @deprecated Use {@link Gallery.text} */
  getDescription() {
    return fskTrans(this.description);
  }

  /**
   * > The `description` is blocked by {@link GalleryData}.
   */
  get text() {
    return fskTrans(this.description);
  }

  get isNotFree() {
    return this.price !== 0;
  }

  /**
   * @type {Date}
   */
  get dateCreated() {
    return new Date(this.published);
  }

  /**
   * @param {Element} element
   */
  [UPGRADE](element) {
    provideGallery(element, this);
  }
}

/**
 * @type {{18: 'image_fsk18'; 16: 'image_fsk16'; 12: 'image_fsk12'}}
 */
const GALLERY_FSK_IMAGE_MAP = {
  18: 'image_fsk18',
  16: 'image_fsk16',
  12: 'image_fsk12',
};

/**
 * Drop those gallery data fields.
 *
 * Remove them from the data source (SOLR).
 *
 * @type {['gratis', 'images', 'image_fsk_list']}
 */
const UNUSED_FIELDS = [
  //
  'gratis',
  'images',
  'image_fsk_list',
];

/**
 * Remove unused gallery data fields, so that no one uses them.
 *
 * @param {Record<string, unknown>} data
 */
export const deleteUnusedFields = (data) =>
  each(UNUSED_FIELDS, (key) => {
    delete data[key];
  });

/**
 * Use 4:3 everywhere (previously 16:9).
 *
 * TODO Implement Frigg formats properly for gallery preview images
 *
 * @param {GalleryData} data
 * @returns {void}
 */
const fixPreviewImage = (data) => {
  const fileSuffix = '-xga.webp';

  for (const level of POSSIBLE_FSK_LEVELS_DESC) {
    const key = GALLERY_FSK_IMAGE_MAP[level];

    if (data[key]) {
      const match = /^(.+)-(preview|large|pw)\.jpe?g/.exec(data[key]);

      ASSERT: guard(match, TUPLE(STRING, STRING, STRING));

      if (match) {
        const [, base, format] = match;

        switch (format) {
          case 'pw':
          case 'preview':
            data[key] = `${base}-${format}${fileSuffix}`;
            break;
          default:
            data[key] = `${base}${fileSuffix}`;
        }
      }
    }
  }
};

export {fixPreviewImage};
